import { Database, TriangleAlert } from "lucide-react";

/**
 * What a data screen shows when it could not read anything.
 *
 * There are two very different reasons a screen comes back empty, and showing
 * the same red "Something went wrong on our side" for both is what makes a
 * perfectly healthy build look broken. On this branch `.env.local` holds
 * placeholders, so *every* data screen fails, the search button appears to do
 * nothing, the categories appear to do nothing, and the app reads as broken
 * when in fact it has simply never been pointed at a database.
 *
 * So:
 *
 * - **Not configured** is not an error. It is a neutral, explained state that
 *   says exactly what is missing and how to fix it. It used to also link to
 *   `/demo`, which is gone now that every screen it previewed exists for real.
 * - **A real failure** keeps the alarm, because then something *is* wrong.
 *
 * `configured` is passed in rather than read here, so this stays a plain
 * component and the environment check lives in one place (`isDatabaseConfigured`).
 */
export function DataUnavailable({
  configured,
  what = "this screen",
}: {
  configured: boolean;
  /** What could not be loaded, e.g. "search results". Used in the copy. */
  what?: string;
}) {
  if (configured) {
    return (
      <div
        role="alert"
        className="flex items-start gap-2.5 rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger"
      >
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden strokeWidth={1.8} />
        <div>Something went wrong on our side. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-line bg-surface p-8 text-center shadow-[var(--shadow-card)]">
      <span
        className="mx-auto mb-4 flex size-12 items-center justify-center rounded-xl bg-accent/10 text-accent"
        aria-hidden
      >
        <Database className="size-6" strokeWidth={1.6} />
      </span>
      <h2 className="text-balance font-display text-lg font-bold text-ink">
        Not connected to a database yet
      </h2>
      <p className="mx-auto mt-2 max-w-md text-pretty text-sm text-muted">
        This build has placeholder credentials, so {what} cannot load. Nothing is
        broken: fill in <code className="font-mono text-body">.env.local</code> with
        the Bookr dev project values and this screen fills itself in.
      </p>
    </div>
  );
}
